import { FC, useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@chakra-ui/react';
import { SoccerBall, Check } from '@phosphor-icons/react';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import { useEndpoints } from '../../../hooks/useEndpoints';
import {
  IFieldWorkout,
  IFieldWorkoutBlock,
  AssignFieldWorkoutBody,
} from '../../../models/field-programming';
import MetadataStep from './steps/MetadataStep';
import BlocksStep from './steps/BlocksStep';
import ExercisesStep from './steps/ExercisesStep';
import AssignStep from './steps/AssignStep';
import styles from './FieldWorkoutBuilderModal.module.scss';

interface Props {
  isOpen: boolean;
  workout?: IFieldWorkout | null; // valorizzato → modifica (no step 4)
  onClose: () => void;
  // Pre-seleziona destinatario in step "Assegna" (uso da scheda atleta singolo).
  preSelectedAthleteId?: string;
}

const STEP_LABELS = ['Metadata', 'Blocchi', 'Esercizi', 'Assegna'];

const FieldWorkoutBuilderModal: FC<Props> = ({ isOpen, workout, onClose, preSelectedAthleteId }) => {
  const api = useEndpoints();
  const qc = useQueryClient();
  const isEdit = !!workout;
  const [step, setStep] = useState(1);

  const [meta, setMeta] = useState({
    name: '',
    description: '',
    sport: '',
    tags: [] as string[],
  });
  const [blocks, setBlocks] = useState<IFieldWorkoutBlock[]>([]);
  const [assignData, setAssignData] = useState<AssignFieldWorkoutBody>({
    scheduledDates: [],
  });

  useEffect(() => {
    if (!isOpen) return;
    if (workout) {
      setMeta({
        name: workout.name,
        description: workout.description ?? '',
        sport: workout.sport ?? '',
        tags: workout.tags,
      });
      setBlocks(workout.blocks);
    } else {
      setMeta({ name: '', description: '', sport: '', tags: [] });
      setBlocks([]);
      setAssignData({
        scheduledDates: [],
        athleteIds: preSelectedAthleteId ? [preSelectedAthleteId] : undefined,
      });
    }
    setStep(1);
  }, [workout, isOpen, preSelectedAthleteId]);

  const createMut = useMutation({
    mutationFn: (body: Partial<IFieldWorkout>) =>
      api.fieldProgramming.workouts.create(body).then((r) => r.data),
  });
  const updateMut = useMutation({
    mutationFn: (body: Partial<IFieldWorkout>) =>
      api.fieldProgramming.workouts.update(workout!._id, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fieldProgramming', 'workouts'] });
      onClose();
    },
  });
  const assignMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AssignFieldWorkoutBody }) =>
      api.fieldProgramming.workouts.assign(id, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fieldProgramming', 'workouts'] });
      qc.invalidateQueries({ queryKey: ['coachSessions'] });
      onClose();
    },
  });

  const canSubmitMeta = meta.name.trim().length > 0;
  const canSubmitAssign = !!(
    (assignData.teamId || (assignData.athleteIds && assignData.athleteIds.length > 0)) &&
    assignData.scheduledDates.length > 0
  );

  const isPending = createMut.isPending || updateMut.isPending || assignMut.isPending;

  const submit = async () => {
    if (isEdit) {
      updateMut.mutate({
        name: meta.name,
        description: meta.description,
        sport: meta.sport || undefined,
        tags: meta.tags,
        blocks,
      });
    } else {
      const created = await createMut.mutateAsync({
        name: meta.name,
        description: meta.description,
        sport: meta.sport || undefined,
        tags: meta.tags,
        blocks,
      });
      assignMut.mutate({ id: created._id, body: assignData });
    }
  };

  const totalSteps = isEdit ? 3 : 4;
  const showAdvanced = step > 1;
  const isFinalStep = (step === 3 && isEdit) || (step === 4 && !isEdit);

  const stepDisplayList = isEdit ? [1, 2, 3] : [1, 2, 3, 4];

  const Header = (
    <div className={styles.header}>
      <div className={styles.headerColorBar} />
      <div className={styles.headerContent}>
        <div className={styles.headerTitleRow}>
          <div className={styles.headerIcon}>
            <SoccerBall size={20} weight="bold" />
          </div>
          <div>
            <p className={styles.headerTitle}>
              {isEdit ? 'Modifica scheda Campo' : 'Nuova scheda Campo'}
            </p>
            <p className={styles.headerSubtitle}>
              {isEdit
                ? 'Modifica metadata, blocchi ed esercizi'
                : 'Configura, costruisci e assegna in pochi step'}
            </p>
          </div>
        </div>

        {/* Stepper */}
        <div className={styles.stepper}>
          {stepDisplayList.map((s, i) => {
            const completed = step > s;
            const active = step === s;
            return (
              <div key={s} className={styles.stepperItemWrapper}>
                <div
                  className={`${styles.stepperItem} ${active ? styles.stepperItemActive : ''} ${completed ? styles.stepperItemCompleted : ''}`}
                >
                  <div className={styles.stepperCircle}>
                    {completed ? <Check size={12} weight="bold" /> : s}
                  </div>
                  <span className={styles.stepperLabel}>{STEP_LABELS[s - 1]}</span>
                </div>
                {i < stepDisplayList.length - 1 && (
                  <div
                    className={`${styles.stepperConnector} ${completed ? styles.stepperConnectorCompleted : ''}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.cancelBtn}
        onClick={onClose}
        disabled={isPending}
      >
        Annulla
      </button>
      <div className={styles.footerActions}>
        {showAdvanced && (
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => setStep((s) => s - 1)}
            disabled={isPending}
          >
            Indietro
          </button>
        )}
        {!isFinalStep && (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setStep((s) => s + 1)}
            disabled={(step === 1 && !canSubmitMeta) || isPending}
          >
            Avanti
          </button>
        )}
        {isFinalStep && (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={submit}
            disabled={(!isEdit && !canSubmitAssign) || isPending}
          >
            {isPending && <Spinner size="xs" />}
            {isEdit ? 'Salva modifiche' : 'Salva e assegna'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      Header={Header}
      Footer={Footer}
      isLoading={isPending}
    >
      <div className={styles.body}>
        {step === 1 && (
          <MetadataStep
            data={meta}
            onChange={(d) => setMeta((m) => ({ ...m, ...d }))}
          />
        )}
        {step === 2 && <BlocksStep blocks={blocks} onChange={setBlocks} />}
        {step === 3 && <ExercisesStep blocks={blocks} onChange={setBlocks} />}
        {step === 4 && !isEdit && (
          <AssignStep
            data={assignData}
            onChange={(d) => setAssignData((p) => ({ ...p, ...d }))}
          />
        )}
      </div>
    </GenericModalV2>
  );
};

export default FieldWorkoutBuilderModal;
